import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { generatePayslip, getEmployees, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../../shared/hooks/TitleContext';

const GeneratePayslip = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { employees, isLoading, error, success } = useSelector((state) => state.auth);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const [formData, setFormData] = useState({
    employee_id: '',
    year: '',
    month: ''
  });

  useEffect(() => {
    setTitle('payslips.generate.title');
  }, [setTitle]);

  useEffect(() => {
    dispatch(getEmployees({ language: i18n.language }));
  }, [dispatch, i18n.language]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      navigate('/payslips');
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
      toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, navigate, isRTL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.employee_id) {
      toast.error(t('payslips.generate.requiredEmployee', { defaultValue: isRTL ? 'يرجى اختيار الموظف' : 'Please select an employee' }), { rtl: isRTL });
      return;
    }
    
    if (!formData.year) {
      toast.error(t('payslips.generate.requiredYear', { defaultValue: isRTL ? 'يرجى إدخال السنة' : 'Please enter the year' }), { rtl: isRTL });
      return;
    }


    const payslipData = {
      employee_id: parseInt(formData.employee_id),
      year: parseInt(formData.year),
      month: parseInt(formData.month)
    };

    dispatch(generatePayslip(payslipData));
  };


  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      <div style={{ marginBottom: '30px' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => navigate('/payslips')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '10px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaArrowLeft />
            {t('payslips.generate.back', { defaultValue: isRTL ? 'العودة' : 'Back' })}
          </button>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold',
            color: 'white',
            margin: 0,
            textAlign
          }}>
            {t('payslips.generate.title', { defaultValue: isRTL ? 'توليد كشف راتب' : 'Generate Payslip' })}
          </h1>
        </div>
      </div>

      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '30px',
        border: '1px solid #333',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <form onSubmit={handleSubmit}>
          {/* الموظف */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: 'white',
              fontWeight: '500',
              textAlign,
              direction: dir
            }}>
              {t('payslips.generate.employee', { defaultValue: isRTL ? 'الموظف' : 'Employee' })} *
            </label>
            <select
              name="employee_id"
              value={formData.employee_id}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '12px 15px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                direction: dir,
                textAlign
              }}
            >
              <option value="" style={{ backgroundColor: '#1a1f2e' }}>{t('payslips.generate.employeePlaceholder', { defaultValue: isRTL ? 'اختر الموظف' : 'Select Employee' })}</option>
              {employees?.map(employee => (
                <option key={employee.id} value={employee.id} style={{ backgroundColor: '#1a1f2e' }}>
                  {(isRTL ? employee.name_ar : employee.name_en) || employee.name} - {employee.employee_code}
                </option>
              ))}
            </select>
          </div>

          {/* السنة */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: 'white',
              fontWeight: '500',
              textAlign,
              direction: dir
            }}>
              {t('payslips.generate.yearMonth', { defaultValue: isRTL ? 'السنة والشهر' : 'Year and Month' })} *
            </label>
            <input
              type="month"
              name="year"
              value={formData.year ? `${formData.year}-${formData.month?.padStart(2, '0') || '01'}` : ''}
              onChange={(e) => {
                const [year, month] = e.target.value.split('-');
                setFormData(prev => ({
                  ...prev,
                  year: year,
                  month: month
                }));
              }}
              style={{
                width: '100%',
                padding: '12px 15px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                direction: dir
              }}
              className="month-input-white"
            />
          </div>


          {/* أزرار الإجراء */}
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
            <button
              type="button"
              onClick={() => navigate('/payslips')}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {t('payslips.generate.cancel', { defaultValue: isRTL ? 'إلغاء' : 'Cancel' })}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? t('payslips.generate.generating', { defaultValue: isRTL ? 'جاري التوليد...' : 'Generating...' }) : t('payslips.generate.generate', { defaultValue: isRTL ? 'توليد كشف الراتب' : 'Generate Payslip' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeneratePayslip;


